import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon, AlertCircle, RotateCw, ExternalLink } from 'lucide-react';

interface ImageLightboxProps {
  isOpen: boolean;
  images: string[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  title?: string;
  sku?: string;
  defaultRotation?: number;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({ 
  isOpen, 
  images, 
  currentIndex, 
  onIndexChange, 
  onClose,
  title,
  sku,
  defaultRotation = 0,
}) => {
  const [zoomState, setZoomState] = useState<{
    show: boolean;
    mouseX: number;
    mouseY: number;
    layerLeft: number;
    layerTop: number;
    zoomedW: number;
    zoomedH: number;
    unrotW: number;
    unrotH: number;
  }>({
    show: false,
    mouseX: 0,
    mouseY: 0,
    layerLeft: 0,
    layerTop: 0,
    zoomedW: 0,
    zoomedH: 0,
    unrotW: 0,
    unrotH: 0,
  });
  const [rotation, setRotation] = useState(defaultRotation);
  const [animateRotation, setAnimateRotation] = useState(false);
  const [brokenUrls, setBrokenUrls] = useState<Set<string>>(new Set());
  const imgRef = useRef<HTMLImageElement>(null);

  const LOUPE_SIZE = 256;
  const LOUPE_HALF = LOUPE_SIZE / 2;
  const ZOOM = 3;

  // Sıfırlama: Diyalog açıldığında veya defaultRotation değiştiğinde animasyonsuz aç
  useEffect(() => {
    if (isOpen) {
      setAnimateRotation(false);
      setRotation(defaultRotation);
      setZoomState(prev => ({ ...prev, show: false }));
    }
  }, [isOpen, defaultRotation]);

  // Görsel değiştiğinde döndürmeyi animasyonsuz resetle
  useEffect(() => {
    setAnimateRotation(false);
    setRotation(defaultRotation);
    setZoomState(prev => ({ ...prev, show: false }));
  }, [currentIndex, defaultRotation]);

  const cleanList = React.useMemo(() => {
    if (!images || !isOpen) return [];
    return images.filter(
      url => url && typeof url === 'string' && url.trim().length > 0 && !brokenUrls.has(url)
    );
  }, [images, isOpen, brokenUrls]);

  const handleRotate = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAnimateRotation(true);
    setRotation(prev => prev + 90);
    setZoomState(prev => ({ ...prev, show: false }));
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && cleanList.length > 1) {
      setZoomState(prev => ({ ...prev, show: false }));
      onIndexChange((currentIndex - 1 + cleanList.length) % cleanList.length);
    }
    if (e.key === 'ArrowRight' && cleanList.length > 1) {
      setZoomState(prev => ({ ...prev, show: false }));
      onIndexChange((currentIndex + 1) % cleanList.length);
    }
    if (e.key.toLowerCase() === 'r') {
      setAnimateRotation(true);
      setRotation(prev => prev + 90);
      setZoomState(prev => ({ ...prev, show: false }));
    }
  }, [isOpen, cleanList.length, currentIndex, onIndexChange, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || cleanList.length === 0) return null;

  const validIndex = Math.min(Math.max(0, currentIndex), cleanList.length - 1);
  const currentImgUrl = cleanList[validIndex];
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const isRotated = normalizedRotation % 180 !== 0;

  const handleMouseMove = (e: React.MouseEvent) => {
    if (window.innerWidth < 768 || !imgRef.current) return;

    const imgRect = imgRef.current.getBoundingClientRect();
    if (imgRect.width === 0 || imgRect.height === 0) return;

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    // Sadece görsel sınırları içindeyken büyüteci göster
    if (
      mouseX < imgRect.left ||
      mouseX > imgRect.right ||
      mouseY < imgRect.top ||
      mouseY > imgRect.bottom
    ) {
      setZoomState(prev => (prev.show ? { ...prev, show: false } : prev));
      return;
    }

    const relX = mouseX - imgRect.left;
    const relY = mouseY - imgRect.top;
    const rx = Math.max(0, Math.min(1, relX / imgRect.width));
    const ry = Math.max(0, Math.min(1, relY / imgRect.height));

    const unrotW = imgRef.current.offsetWidth;
    const unrotH = imgRef.current.offsetHeight;
    if (unrotW === 0 || unrotH === 0) return;

    const zoomedW = imgRect.width * ZOOM;
    const zoomedH = imgRect.height * ZOOM;

    const layerLeft = LOUPE_HALF - rx * zoomedW;
    const layerTop = LOUPE_HALF - ry * zoomedH;

    setZoomState({
      show: true,
      mouseX,
      mouseY,
      layerLeft,
      layerTop,
      zoomedW,
      zoomedH,
      unrotW,
      unrotH,
    });
  };

  const handleImageError = (failedUrl: string) => {
    setBrokenUrls(prev => {
      const nextSet = new Set(prev);
      nextSet.add(failedUrl);
      return nextSet;
    });
    if (validIndex >= cleanList.length - 1 && validIndex > 0) {
      onIndexChange(validIndex - 1);
    }
  };

  const openRotatedHtmlWindow = (url: string, rot: number, pageTitle?: string) => {
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${pageTitle || 'Görsel İnceleme'}</title>
            <style>
              body {
                margin: 0;
                background-color: #0f172a;
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                overflow: auto;
              }
              img {
                transform: rotate(${rot}deg);
                max-width: 90vw;
                max-height: 90vh;
                object-fit: contain;
                border-radius: 12px;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
              }
            </style>
          </head>
          <body>
            <img src="${url}" alt="Görsel" />
          </body>
        </html>
      `);
      w.document.close();
    }
  };

  const handleOpenBlank = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentImgUrl) return;

    if (normalizedRotation === 0) {
      window.open(currentImgUrl, '_blank');
      return;
    }

    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = currentImgUrl;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        openRotatedHtmlWindow(currentImgUrl, normalizedRotation, title);
        return;
      }

      const rads = (normalizedRotation * Math.PI) / 180;
      const isOrthogonal = (normalizedRotation % 180) !== 0;

      canvas.width = isOrthogonal ? img.height : img.width;
      canvas.height = isOrthogonal ? img.width : img.height;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rads);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        } else {
          openRotatedHtmlWindow(currentImgUrl, normalizedRotation, title);
        }
      });
    } catch {
      openRotatedHtmlWindow(currentImgUrl, normalizedRotation, title);
    }
  };

  const next = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setZoomState(p => ({ ...p, show: false }));
    onIndexChange((validIndex + 1) % cleanList.length);
  };
  
  const prev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setZoomState(p => ({ ...p, show: false }));
    onIndexChange((validIndex - 1 + cleanList.length) % cleanList.length);
  };

  return (
    <div 
      className="fixed inset-0 z-[99999] flex flex-col items-center justify-between bg-black/85 backdrop-blur-xl animate-in fade-in duration-200 select-none p-3 sm:p-6"
      onClick={onClose}
    >
      {/* Üst Bar: Başlık, SKU, Döndür, Orijinal Aç, Sayaç ve Kapat */}
      <div 
        className="w-full max-w-6xl flex items-center justify-between gap-3 z-[100] animate-in slide-in-from-top-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0 bg-white/10 dark:bg-black/40 backdrop-blur-md px-3 sm:px-4 py-2 rounded-2xl border border-white/15 shadow-xl">
          <div className="w-8 h-8 rounded-xl bg-primary/20 text-primary flex items-center justify-center shrink-0">
            <ImageIcon size={18} className="text-white" />
          </div>
          <div className="flex flex-col min-w-0">
            {sku && (
              <span className="text-[10px] font-black text-white/70 tracking-wider uppercase font-mono truncate">
                {sku}
              </span>
            )}
            <span className="text-xs sm:text-sm font-bold text-white truncate max-w-xs sm:max-w-md">
              {title || "Görsel İnceleme"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Döndür Butonu */}
          <button
            type="button"
            onClick={handleRotate}
            className="w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white/80 hover:text-white flex items-center justify-center transition-all border border-white/15 shadow-xl hover:scale-105 active:scale-95 cursor-pointer"
            title="Görseli Sağa Döndür (R)"
          >
            <RotateCw size={18} />
          </button>

          {/* Orijinal Boyutta Yeni Sekmede Aç */}
          <button
            type="button"
            onClick={handleOpenBlank}
            className="w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white/80 hover:text-white flex items-center justify-center transition-all border border-white/15 shadow-xl hover:scale-105 active:scale-95 cursor-pointer"
            title="Görseli Döndürülmüş Haliyle Yeni Sekmede Aç"
          >
            <ExternalLink size={18} />
          </button>

          {/* Görsel Sayaç Rozeti */}
          {cleanList.length > 0 && (
            <div className="flex items-center px-3 py-1.5 bg-white/10 dark:bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 text-white/90 text-xs font-black tracking-wider shadow-lg">
              <span>{validIndex + 1}</span>
              <span className="opacity-40 mx-1.5">/</span>
              <span className="opacity-70">{cleanList.length}</span>
            </div>
          )}

          {/* Kapat Butonu */}
          <button 
            type="button"
            onClick={onClose} 
            className="w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white/80 hover:text-white flex items-center justify-center transition-all border border-white/15 shadow-xl hover:scale-105 active:scale-95 cursor-pointer"
            title="Kapat (Esc)"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Ana Görsel Sahnesi */}
      <div 
        className="relative flex items-center justify-center w-full max-w-6xl flex-1 my-2 group"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sol Ok */}
        {cleanList.length > 1 && (
          <button 
            type="button"
            onClick={prev} 
            className="absolute left-2 sm:left-4 z-[101] w-12 h-12 sm:w-14 sm:h-14 bg-black/40 hover:bg-primary text-white rounded-2xl backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl transition-all hover:scale-110 active:scale-95 cursor-pointer"
            title="Önceki Görsel (Sol Ok)"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {/* Resim Alanı & Mercek */}
        {currentImgUrl ? (
          <div 
            className="relative flex items-center justify-center p-2 sm:p-3 rounded-3xl bg-white/5 border border-white/10 shadow-2xl"
            style={{
              maxHeight: '70vh',
              maxWidth: '92vw',
              transition: animateRotation ? 'all 0.3s ease-out' : 'none',
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setZoomState(p => ({ ...p, show: false }))}
          >
            <img
              ref={imgRef}
              src={currentImgUrl}
              alt={title || "Görsel"}
              onError={() => handleImageError(currentImgUrl)}
              style={{
                transform: `rotate(${rotation}deg)`,
                maxHeight: isRotated ? '52vw' : '62vh',
                maxWidth: isRotated ? '60vh' : '85vw',
                margin: isRotated ? '2.5rem 1.5rem' : '0 auto',
                transition: animateRotation 
                  ? 'transform 0.3s ease-out, max-height 0.3s ease-out, max-width 0.3s ease-out, margin 0.3s ease-out' 
                  : 'none',
              }}
              className="w-auto object-contain rounded-2xl pointer-events-auto select-none"
            />

            {/* Dinamik Masaüstü Büyüteç / Mercek (Çevrilmiş Görsel Desteği ile) */}
            {zoomState.show && (
              <div 
                className="pointer-events-none fixed z-[999999] w-64 h-64 rounded-2xl border-2 border-white/70 shadow-[0_0_50px_rgba(0,0,0,0.85)] overflow-hidden hidden md:block bg-slate-900"
                style={{
                  top: `${zoomState.mouseY}px`,
                  left: `${zoomState.mouseX}px`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <div 
                  style={{
                    position: 'absolute',
                    left: `${zoomState.layerLeft}px`,
                    top: `${zoomState.layerTop}px`,
                    width: `${zoomState.zoomedW}px`,
                    height: `${zoomState.zoomedH}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={currentImgUrl}
                    alt=""
                    draggable={false}
                    style={{
                      width: `${zoomState.unrotW * ZOOM}px`,
                      height: `${zoomState.unrotH * ZOOM}px`,
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                      maxWidth: 'none',
                      maxHeight: 'none',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  />
                </div>

                {/* Büyüteç Nişangah / Artı Çizgileri */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-full h-px bg-white/20 absolute" />
                  <div className="w-px h-full bg-white/20 absolute" />
                  <div className="w-4 h-4 rounded-full border border-white/40 absolute" />
                  <div className="w-1 h-1 rounded-full bg-primary absolute" />
                </div>

                {/* Büyütme Oranı Rozeti (Sol Üst) */}
                <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-[10px] font-black text-white/90 tracking-wider">
                  {ZOOM}x
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 p-8 rounded-3xl bg-white/5 border border-white/10 text-white/70">
            <AlertCircle size={40} className="text-amber-400" />
            <span className="text-sm font-bold">Görüntülenebilir geçerli görsel bulunamadı.</span>
          </div>
        )}

        {/* Sağ Ok */}
        {cleanList.length > 1 && (
          <button 
            type="button"
            onClick={next} 
            className="absolute right-2 sm:right-4 z-[101] w-12 h-12 sm:w-14 sm:h-14 bg-black/40 hover:bg-primary text-white rounded-2xl backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl transition-all hover:scale-110 active:scale-95 cursor-pointer"
            title="Sonraki Görsel (Sağ Ok)"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>

      {/* Alt Thumbnail Şeridi */}
      {cleanList.length > 1 ? (
        <div 
          className="flex items-center gap-2.5 p-2 bg-white/10 dark:bg-black/50 backdrop-blur-2xl rounded-2xl border border-white/15 shadow-2xl max-w-full overflow-x-auto no-scrollbar animate-in slide-in-from-bottom-4 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {cleanList.map((url, idx) => {
            const isSelected = validIndex === idx;
            return (
              <button
                key={url + idx}
                type="button"
                onClick={() => onIndexChange(idx)}
                className={`relative shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden transition-all duration-200 border-2 cursor-pointer ${
                  isSelected 
                    ? 'border-primary shadow-lg scale-105 ring-2 ring-primary/50' 
                    : 'border-white/20 opacity-50 hover:opacity-90 hover:scale-100'
                }`}
              >
                <img 
                  src={url} 
                  onError={() => handleImageError(url)}
                  className="w-full h-full object-cover" 
                  alt={`Thumbnail ${idx + 1}`} 
                />
                {isSelected && (
                  <div className="absolute inset-0 bg-primary/15 pointer-events-none" />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="h-2" />
      )}
    </div>
  );
};
